import type { StoreAddressFragment, StoreInfoFragment } from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { Brand, getBrandByBrandName } from '@shared/domain/brand'
import { Domain } from '@shared/gql/document-nodes'
import { ArrowRightOutline24, GroceriesOutline24, LocationOutline24 } from '@sok/design-system'
import { BrandStoreLogo } from 'components/BrandStoreLogo'
import { NextLink } from 'components/NextLink'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  StoreInfoCard,
  StoreInfoCardItem,
  StoreInfoCardItemWrapper,
} from 'views/store/components/StoreInfoCard'
import { useTodaysOpeningTime } from 'views/store/components/StoreOpeningTimes/StoreOpeningTimes'
import { TodaysOpeningTime } from 'views/store/components/StoreOpeningTimes/TodayOpeningTime'

const LogoContainer = styled(StoreInfoCardItem)(({ theme }) => ({
  height: theme.spacings.large,
}))

const StyledStoreInfoCardItemWrapper = styled(StoreInfoCardItemWrapper)(({ theme }) => ({
  color: theme.colors.grey800,
}))

const ArrowIcon = styled(ArrowRightOutline24)({
  position: 'absolute',
  right: '0',
  top: '50%',
  transform: 'translateY(-50%)',
})

const formatAddress = (address: StoreAddressFragment | null | undefined): string => {
  if (!address) {
    return ''
  }

  const postalArea = [address.postcode, address.postcodeName?.default].filter(Boolean).join(' ')
  return [address.street.default, postalArea].filter(Boolean).join(', ')
}

const StoreLink: React.FC<{ store: StoreInfoFragment }> = ({ store }) => {
  const { t } = useTranslation()
  const todaysOpeningTime = useTodaysOpeningTime(store.weeklyOpeningHours?.[0].openingTimes ?? [])

  const brand = getBrandByBrandName(store.brand) || Brand.Eprisma

  return (
    <NextLink href={`/myymala/${store.slug}/${store.id}`} data-test-id="store-list-item">
      <StoreInfoCard>
        <StyledStoreInfoCardItemWrapper>
          <LogoContainer>
            <BrandStoreLogo
              alt={store.brand ? t(store.brand) : undefined}
              brand={brand}
              data-test-id="brand-logo"
            />
          </LogoContainer>
          <StoreInfoCardItem data-test-id="store-title">{store.name}</StoreInfoCardItem>
          <StoreInfoCardItem>
            <LocationOutline24 />
            <span>{formatAddress(store.location?.address)}</span>
          </StoreInfoCardItem>
          {todaysOpeningTime && <TodaysOpeningTime openingTime={todaysOpeningTime} />}
          {store.domains.includes(Domain.SKaupat) && (
            <StoreInfoCardItem>
              <GroceriesOutline24 />
              <span>{t('Order online')}</span>
            </StoreInfoCardItem>
          )}
        </StyledStoreInfoCardItemWrapper>

        <ArrowIcon />
      </StoreInfoCard>
    </NextLink>
  )
}

const _StoreList: FC<{ stores: StoreInfoFragment[]; className?: string }> = ({
  stores,
  className,
}) => {
  return (
    <ul className={className}>
      {stores.map((store) => (
        <li key={store.id}>
          <StoreLink store={store} />
        </li>
      ))}
    </ul>
  )
}

export const StoreList = styled(_StoreList)({
  listStyleType: 'none',
})
